/**
 * Настройки рекламы поверхности.
 *
 * Одним объектом, а не колонками: у каждого места свой набор параметров —
 * код для компьютера и для телефона, список экранов, моменты показа, — и
 * раскладывать их по столбцам значило бы держать в схеме три десятка полей,
 * половина которых для конкретного места не имеет смысла.
 *
 * Формат один для всех поверхностей; в мини-приложении осмысленна только часть
 * мест, и панель показывает соответственно.
 */
export const AD_INTERSTITIAL_DEFAULT_INTERVAL_SEC = 180;
const emptyCode = () => ({ desktop: null, mobile: null });
const emptyScreenCodes = () => ({
    landing: null,
    onboarding: null,
    search: null,
    chat: null,
    saved: null,
});
export function emptyAdsConfig() {
    return {
        landing: { code: emptyCode() },
        onboarding: { code: emptyCode() },
        search: { code: emptyCode() },
        sideLeft: { code: emptyScreenCodes() },
        sideRight: { code: emptyScreenCodes() },
        interstitial: {
            code: emptyCode(),
            onChatEnd: true,
            onSearchStart: false,
            minIntervalSec: AD_INTERSTITIAL_DEFAULT_INTERVAL_SEC,
        },
    };
}
/** Названия мест на экранах — для перебора в интерфейсе и на клиенте. */
export const AD_SCREEN_SLOTS = ['landing', 'onboarding', 'search'];
export const AD_SIDE_SLOTS = ['sideLeft', 'sideRight'];
/**
 * Идентификаторы контейнеров, которые встречаются в двух разных местах.
 *
 * Сети привязывают блок к контейнеру по идентификатору и рисуют в первый
 * найденный. Один и тот же код, вставленный в два места, поэтому даёт ровно
 * одно объявление, а второе место остаётся пустым — и понять это по внешнему
 * виду невозможно. Для второго места в кабинете сети заводится отдельный блок.
 *
 * Внутри одного места варианты для компьютера и телефона не конфликтуют:
 * одновременно показывается только один из них.
 */
export function findDuplicateAdIds(ads) {
    const places = [
        codeIds(ads.landing.code),
        codeIds(ads.onboarding.code),
        codeIds(ads.search.code),
        ...screenCodeIds(ads.sideLeft.code),
        ...screenCodeIds(ads.sideRight.code),
        codeIds(ads.interstitial.code),
    ];
    const usage = new Map();
    for (const ids of places) {
        for (const id of new Set(ids)) {
            usage.set(id, (usage.get(id) ?? 0) + 1);
        }
    }
    return [...usage.entries()].filter(([, count]) => count > 1).map(([id]) => id);
}
function markupIds(code) {
    if (!code) {
        return [];
    }
    return [...code.matchAll(/\bid\s*=\s*["']([^"']+)["']/g)].map((match) => match[1] ?? '');
}
/** Экраны полосы считаются разными местами: сеть не рисует один блок дважды. */
function screenCodeIds(codes) {
    return Object.values(codes).map((code) => markupIds(code));
}
function codeIds(code) {
    return [...markupIds(code.desktop), ...markupIds(code.mobile)];
}
/**
 * Цели Яндекс.Метрики, о которых сообщает само приложение.
 *
 * Визуальный выбор элемента в Метрике здесь не работает: страницу нельзя
 * встроить в рамку (защита от кликджекинга), а привязка к вёрстке ломалась бы
 * при первой же правке. Поэтому цели — события, а идентификаторы заданы здесь:
 * их же администратор заводит в Метрике вручную.
 */
export const METRIKA_GOALS = {
    /** Нажата кнопка «Начать чат» на главной. */
    START_CLICK: 'start_click',
    /** Анкета заполнена, поиск запущен. */
    SEARCH_STARTED: 'search_started',
    /** Собеседник найден. */
    CHAT_MATCHED: 'chat_matched',
    /** Отправлено первое сообщение в диалоге. */
    FIRST_MESSAGE: 'first_message',
};
//# sourceMappingURL=ads.js.map